import React from "react";
import { Box, Typography } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const COLORS = [
  "#3592FB", // سفارش جدید
  "rgba(154, 64, 224, 1)", // خرابی
  "rgba(76, 175, 80, 1)", // تجدید ساخت
  "rgba(255, 152, 0, 1)", // اصلاحیه
  "#607D8B",
  "#9C27B0",
];

const renderExternalLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  name,
  fill,
  index,
}) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);

  // line start
  const sx = cx + outerRadius * cos;
  const sy = cy + outerRadius * sin;

  // line end
  const ex = cx + (outerRadius + 20) * cos;
  const ey = cy + (outerRadius + 20) * sin;

  // vertical spacing (prevents sticking)
  const verticalOffset = (index % 2 === 0 ? 1 : -1) * 14;

  // label position
  const lx = ex + (cos >= 0 ? 14 : -14);
  const ly = ey + verticalOffset;

  const textAnchor = cos >= 0 ? "start" : "end";

  return (
    <g>
      {/* connector line */}
      <line x1={sx} y1={sy} x2={ex} y2={ey} stroke={fill} strokeWidth={1} />

      {/* dot */}
      <circle cx={ex} cy={ey} r={3} fill={fill} />

      {/* label background */}
      <rect
        x={lx + (cos >= 0 ? 0 : -95)}
        y={ly - 11}
        width={95}
        height={22}
        rx={6}
        fill="#fff"
        opacity={0.9}
      />

      {/* label text */}
      <text
        x={lx}
        y={ly}
        textAnchor={textAnchor}
        dominantBaseline="central"
        fontSize={12}
        fill="#333"
        fontWeight={500}
      >
        {name} {(percent * 100).toFixed(1)}%
      </text>
    </g>
  );
};

function ChartRenderer({ data, totalRecord }) {
  const chartFormatter = new Intl.NumberFormat("fa-IR");
  const toPersianDigits = (str) => {
    if (!str) return "";
    return str.toString().replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);
  };
  const pieData = data?.items || data || [];
  const hasData = pieData.length > 0;

  // If no data, use placeholder
  const displayData = hasData ? pieData : [{ name: "بدون اطلاعات", value1: 1 }];
  const total = hasData
    ? pieData.reduce((sum, item) => sum + item.value, 0)
    : 1;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "flex-start",
        flexWrap: "nowrap",
        width: "100%",
        overflowX: { xs: "auto", md: "visible" },
        WebkitOverflowScrolling: "touch",
      }}
    >
      {/* Pie Chart */}
      <Box
        sx={{
          minWidth: 320,
          width: { xs: 320, md: "60%" },
          height: 360,
          flexShrink: 0,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={displayData}
              dataKey="value1"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={110}
              innerRadius={55}
              paddingAngle={3}
              // label={hasData ? renderExternalLabel : false}
              labelLine={false}
            >
              {displayData.map((_, index) => (
                <Cell
                  key={index}
                  fill={hasData ? COLORS[index % COLORS.length] : "#ccc"}
                />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload?.length) {
                  const d = payload[0];
                  const color = hasData
                    ? COLORS[
                        pieData.findIndex((p) => p.name === d.name) %
                          COLORS.length
                      ]
                    : "#ccc";
                  return (
                    <Box
                      sx={{
                        background: "#fff",
                        padding: 1,
                        border: "1px solid #ccc",
                        borderRadius: 1,
                      }}
                    >
                      <Typography sx={{ color, fontWeight: 500 }}>
                        {toPersianDigits(d.name)}:{" "}
                        {hasData
                          ? toPersianDigits(d.value)
                          : toPersianDigits(0)}
                      </Typography>
                    </Box>
                  );
                }
                return null;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
  <Box
         sx={{ width: "100%", display: "flex", padding: "4px", justifyContent: "center" }}
       >
      {/* Custom Legend */}
      <Box
        sx={{
          display: "flex",
          width: "60%",
          height: "340px",
          flexDirection: "column",
          overflowY: "auto",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, textAlign: "center",
        alignItems: "center" }}>
          <Typography sx={{ fontWeight: "bold", flex:1, px: 1 }}>نوع فروش</Typography>
          <Typography sx={{ fontWeight: "bold", flex:1, px: 1 }}>درصد</Typography>
          <Typography sx={{ fontWeight: "bold", flex:1, px: 1 }}>متراژ</Typography>
        </Box>
        {displayData.map((item, index) => {
          const color = hasData ? COLORS[index % COLORS.length] : "#ccc";
          const percent = hasData
            ? ((item.value / total) * 100).toFixed(2) + "%"
            : "0%";
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                textAlign: "center", 
              }}
            >
              <Typography sx={{ color, flex:1, textAlign: "center" }} px={1}>
                {toPersianDigits(item.name)}
              </Typography>
              <Typography sx={{ flex:1, textAlign: "center" }} px={1}>
                {toPersianDigits(percent)}
              </Typography>
              <Typography sx={{ flex:1, textAlign: "center" }} px={1}>
                {chartFormatter.format(item?.value1)}
              </Typography>
            </Box>
          );
        })}
      </Box>
      </Box>
    </Box>
  );
}

export default ChartRenderer;
